import React, { useState, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import TextField from '@mui/material/TextField';
import styled from 'styled-components';
import useAuth from '../../../../features/hooks/useAuth';
import { useGetUsersQuery } from '../../../../features/users/usersApiSlice';
import {  useUpdateassetReqMutation } from '../../../../features/assetrequest/assetReqApiSlice';
import { useGetStoreOfficesQuery } from '../../../../features/storeoffices/storeofficesApiSlice';
import { useGetofficesQuery } from '../../../../features/offices/officesApiSlice';
import {  CommonButton } from '../../../../Styles/CommonStyles';
import nameStringFn from '../../../../data/nameStringFn';
import Chip from '@mui/material/Chip';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import CircularProgress from '@mui/material/CircularProgress';
import { useLazyGetAssetsFinalFilteredQuery } from '../../../../features/addassets/addassetfinalApiSlice';

const Container = styled.div`
  margin-left: 10px;
  margin-right: 10px;
`;

const ButtonCont = styled.div`
  display: flex;
  margin-top: 10px;
  margin-bottom: 40px;
  gap: 15px;
`;

const ForwardButton = styled(CommonButton)`
  background-color: #4caf50;
  
  &:hover {
    background-color: #45a049;
  }
`;

const RejectButton = styled(CommonButton)`
  background-color: #f44336;
  
  &:hover {
    background-color: #d32f2f;
  }
`;

const RemarkContainer = styled.div`
  margin-top: 15px;
  width: 100%;
`;

const TransferInfoContainer = styled.div`
  background-color: #f5f5f5;
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 20px;
  border-left: 4px solid #2196f3;
`;

const TransferInfoTitle = styled.h4`
  margin-top: 0;
  margin-bottom: 10px;
  color: #1976d2;
`;

const TransferInfoText = styled.p`
  margin: 5px 0;
  color: #333;
`;

const Bo_UnitHeadReturnInboxForm = ({ row, showActionOptions, handleBack }) => {
  const [remark, setRemark] = useState('');
  const [assetDetails, setAssetDetails] = useState({});
  const [processing, setProcessing] = useState(false);
  const editor = useRef(null);
  const navigate = useNavigate();

  // Get logged in user data
  const { id, designation, designationSuffix, numSuffix, roles } = useAuth();
  const user_current_office = useSelector((state) => state.office.off);

  // Get user data
  const { getSingleuser } = useGetUsersQuery('userslist', {
    selectFromResult: ({ data }) => ({ getSingleuser: data?.entities }),
  });

  // Get store offices data
  const { storeoffices } = useGetStoreOfficesQuery('storeoffices', {
    selectFromResult: ({ data }) => ({ storeoffices: data?.entities }),
  });

  // Get office data for BO ID
  const { officedata } = useGetofficesQuery('offices', {
    selectFromResult: ({ data }) => ({
      officedata: data?.entities
    }),
  });

  // Asset fetch API
  const [fetchFilteredAssets] = useLazyGetAssetsFinalFilteredQuery();

  // Update request mutation
  const [updateRequest] = useUpdateassetReqMutation();

  const BO_id = officedata ? Object.values(officedata)?.find(office => office?.name === user_current_office)?.Bo : "";

  // Check if this is a transfer request
  const isTransferRequest = row?.req_type === "BO_User_Transfer" && row?.curr_sent_to_tag === "bo_user_transfer_unithead_approval";
  

  // Handle forwarding to store
  const handleForwardToStore = async () => {
    console.log("Entered")
    if (!remark.trim()) {
      alert("Please provide a remark");
      return;
    }
    
    if (!row) {
      alert("Request data not found");
      return;
    }
    
    try {
      setProcessing(true);
      
      const targetStore = row.req_store;
      const storeLevelId = storeoffices ? Object.values(storeoffices)?.find(office => office?.storeoffice === targetStore)?.roleUsers?.L1ReqApprover_A : null;
      console.log(storeLevelId)
      console.log(targetStore)
      if (!storeLevelId) {
        setProcessing(false);
        return alert("Store Level 1 approver not found for the selected store");
      }

      const senttoData = {
        sent_to_tag: "botostore_return_req_forwarded",
        Text: remark,
        sent_by: { 
          id, 
          name: getSingleuser?.[id]?.name, 
          office: user_current_office, 
          designation: designation[user_current_office], 
          designationSuffix: designationSuffix[user_current_office], 
          numSuffix: numSuffix[user_current_office] 
        },
        sent_to: { 
          id: storeLevelId,
          name: getSingleuser?.[storeLevelId]?.name,
          office: targetStore,
          designation: getSingleuser?.[storeLevelId]?.designation?.[targetStore] || 'Level 1 Approver'
        },
        curr_sent_to_string: `${nameStringFn(getSingleuser?.[storeLevelId])}-${targetStore}-${getSingleuser?.[storeLevelId]?.designation?.[targetStore] || 'Unknown'}`,
        action: 'BO Asset Return Request Approved by Unit Head, Forwarded to Store',
        time_stamp: new Date(),
        opened: false
      };

      const body = { id: row.id, senttoData: senttoData };

      await updateRequest(body);

      setProcessing(false);
      handleBack();
      alert('Request approved and forwarded to Store Level 1 Approver');
    } catch (error) {
      setProcessing(false);
      console.error("Error forwarding to store:", error);
      alert(`Error: ${error.message || 'Failed to forward request'}`);
    }
  };
  
  // Handle rejecting the request
  const handleRejectRequest = async () => {
    if (!remark.trim()) {
      alert("Please provide a reason for rejection");
      return;
    }
    
    if (!row) {
      alert("Request data not found");
      return;
    }
    
    try {
      setProcessing(true);
      
      const senttoData = {
        sent_to_tag: "botostore_return_req_rejected",
        Text: remark,
        sent_by: { 
          id, 
          name: getSingleuser?.[id]?.name, 
          office: user_current_office, 
          designation: designation[user_current_office], 
          designationSuffix: designationSuffix[user_current_office], 
          numSuffix: numSuffix[user_current_office] 
        },
        sent_to: { 
          id: row.req_creator_id,
          name: getSingleuser?.[row.req_creator_id]?.name,
          office: row?.req_data?.[0]?.sent_by?.office || 'Unknown Office',
          designation: row?.req_data?.[0]?.sent_by?.designation || 'BO'
        },
        curr_sent_to_string: `${nameStringFn(getSingleuser?.[row.req_creator_id])}-${row.req_data[0]?.sent_by?.office}-${row.req_data[0]?.sent_by?.designation || 'Unknown'}`,
        action: 'BO Asset Return Request Rejected by Unit Head',
        time_stamp: new Date(),
        curr_status: 'Rejected',
      };

      const body = { id: row.id, senttoData: senttoData };

      await updateRequest(body);

      setProcessing(false);
      handleBack();
      alert('Request rejected');
    } catch (error) {
      setProcessing(false);
      console.error("Error rejecting request:", error);
      alert(`Error: ${error.message || 'Failed to reject request'}`);
    }
  };

  // Handle approving transfer request
  const handleApproveTransfer = async () => {
    if (!remark.trim()) {
      alert("Please provide a remark before approving the transfer");
      return;
    }

    if (!row?.final_transfer_user) {
      alert("Final transfer user information not found");
      return;
    }

    try {
      setProcessing(true);

      // Create senttoData for approving and forwarding to user
      let senttoData = {
        sent_to_tag: "uh_apprvd_bo_user_transfer",
        Text: remark,
        action: "BO to User Asset Transfer Request Approved by Unit Head",
        sent_by: { 
          id: id, 
          name: getSingleuser?.[id]?.name, 
          office: user_current_office, 
          designation: designation?.[user_current_office],
          designationSuffix: designationSuffix?.[user_current_office],
          numSuffix: numSuffix?.[user_current_office]
        },
        sent_to: {
          id: row.final_transfer_user.id,
          name: row.final_transfer_user.name,
          office: user_current_office,
          designation: row.final_transfer_user.designation
        },
        curr_sent_to: row.final_transfer_user.id,
        curr_sent_to_string: `${nameStringFn(row.final_transfer_user)}-${user_current_office}-${row.final_transfer_user.designation || 'Unknown'}`,
        curr_sent_to_tag: 'new_bo_transfer_req',
        time_stamp: new Date(),
        opened: false
      };
      
      const body = { id: row?._id, senttoData: senttoData };
      
      await updateRequest(body);
      setProcessing(false);
      alert("Transfer request has been approved and forwarded to the user.");
      handleBack();
    } catch (error) {
      setProcessing(false);
      console.error("Error approving transfer request:", error);
      alert("Error approving transfer request. Please try again.");
    }
  };

  // Handle rejecting transfer request
  const handleRejectTransfer = async () => {
    if (!remark.trim()) {
      alert("Please provide a reason for rejecting the transfer");
      return;
    }

    try {
      setProcessing(true);

      // Create senttoData for rejecting and returning to BO
      let senttoData = {
        sent_to_tag: "unithead_rejected_bo_user_transfer",
        Text: remark,
        action: "BO to User Asset Transfer Request Rejected by Unit Head",
        sent_by: { 
          id: id, 
          name: getSingleuser?.[id]?.name, 
          office: user_current_office, 
          designation: designation?.[user_current_office],
          designationSuffix: designationSuffix?.[user_current_office],
          numSuffix: numSuffix?.[user_current_office]
        },
        sent_to: {
          id: BO_id,
          name: getSingleuser?.[BO_id]?.name,
          office: user_current_office,
          designation: getSingleuser?.[BO_id]?.designation?.[user_current_office] || 'Unknown'
        },
        curr_sent_to: BO_id,
        curr_sent_to_string: `${nameStringFn(getSingleuser?.[BO_id])}-${user_current_office}-${getSingleuser?.[BO_id]?.designation?.[user_current_office] || 'Unknown'}`,
        curr_sent_to_tag: 'bo_user_transfer_rejected_by_unithead',
        curr_status: "Rejected",
        time_stamp: new Date()
      };

      const body = { id: row?._id, senttoData: senttoData };
      
      await updateRequest(body);
      setProcessing(false);
      alert("Transfer request has been rejected and returned to BO.");
      handleBack();
    } catch (error) {
      setProcessing(false);
      console.error("Error rejecting transfer request:", error);
      alert("Error rejecting transfer request. Please try again.");
    }
  };

  // Check if this request is meant for the current user (Unit Head)
  const isRequestForCurrentUser = row?.curr_sent_to === id;

  useEffect(() => {
    if (row && !isRequestForCurrentUser) {
      navigate('/management/assets/myrequests/inbox');
      alert('This request is not assigned to you');
    }
  }, [row, isRequestForCurrentUser, navigate]);

  if (!row) return <div>Request not found</div>;
  if (!isRequestForCurrentUser) return null;

  return (
    <Container>  
      {showActionOptions? <>
      {/* Display transfer information for transfer requests */}
      {isTransferRequest && (
        <TransferInfoContainer>
          <TransferInfoTitle>Transfer Request Details (BO Assets and BO Role)</TransferInfoTitle>
          <TransferInfoText>
            <strong>From:</strong> {nameStringFn(getSingleuser?.[row?.req_creator_id])} ({getSingleuser?.[row?.req_creator_id]?.designation?.[user_current_office] || 'Branch Officer'})
          </TransferInfoText>
          <TransferInfoText>
            <strong>To:</strong> {nameStringFn(row?.final_transfer_user)} ({row?.final_transfer_user?.designation || 'Officer'})
          </TransferInfoText>
          <TransferInfoText>
            <strong>Office:</strong> {user_current_office}
          </TransferInfoText>
        </TransferInfoContainer>
      )}
      
      <RemarkContainer>
        <TextField
          fullWidth
          multiline
          rows={4}
          label="Add Remark"
          variant="outlined"
          value={remark}
          onChange={(e) => setRemark(e.target.value)}
        />
      </RemarkContainer>
      
      <ButtonCont>
        {isTransferRequest ? (
          // Transfer request buttons
          <>
            <ForwardButton 
              onClick={handleApproveTransfer} 
              startIcon={processing ? <CircularProgress size={20} color="inherit" /> : <CheckCircleIcon />}
              disabled={processing}
            >
              {processing ? 'Processing...' : 'Approve Transfer'}
            </ForwardButton>
            <RejectButton 
              onClick={handleRejectTransfer} 
              startIcon={processing ? <CircularProgress size={20} color="inherit" /> : <CancelIcon />}
              disabled={processing}
            >
              {processing ? 'Processing...' : 'Reject Transfer'}
            </RejectButton>
          </>
        ) : (
          // Return request buttons
          <>
            <ForwardButton 
              onClick={handleForwardToStore} 
              startIcon={processing ? <CircularProgress size={20} color="inherit" /> : <CheckCircleIcon />}
              disabled={processing}
            >
              {processing ? 'Processing...' : 'Forward to Store'}
            </ForwardButton>
            <RejectButton 
              onClick={handleRejectRequest} 
              startIcon={processing ? <CircularProgress size={20} color="inherit" /> : <CancelIcon />}
              disabled={processing}
            >
              {processing ? 'Processing...' : 'Reject Request'}
            </RejectButton>
          </>
        )}
      </ButtonCont></> : null}
    </Container>
  );
};

export default Bo_UnitHeadReturnInboxForm; 